import React from 'react';
import Card from "../Card/Card";
import NoData from "../NoData/NoData";
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';

const CardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm border-l-4 border-l-gray-200 overflow-hidden animate-pulse">
        {/* Header row */}
        <div className="flex items-start gap-2.5 px-3 pt-3 pb-2">
            <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
            <div className="flex-1 space-y-2 mt-0.5">
                <div className="flex items-center gap-2">
                    <div className="h-3.5 bg-gray-200 rounded-md flex-1" />
                    <div className="h-4 w-14 bg-gray-200 rounded-md flex-shrink-0" />
                </div>
                <div className="h-2.5 bg-gray-200 rounded-md w-2/3" />
                <div className="h-2.5 bg-gray-200 rounded-md w-1/2" />
            </div>
        </div>
        {/* Pills */}
        <div className="flex gap-2 px-3 pb-3">
            <div className="h-4 w-20 bg-gray-200 rounded-md" />
            <div className="h-4 w-14 bg-gray-200 rounded-md" />
            <div className="h-4 w-10 bg-gray-200 rounded-md" />
        </div>
        {/* Action bar */}
        <div className="border-t border-gray-100 grid grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
                <div
                    key={i}
                    className={`flex flex-col items-center justify-center py-2.5 gap-1 ${i > 0 ? 'border-l border-gray-100' : ''}`}
                >
                    <div className="w-4 h-4 bg-gray-200 rounded" />
                    <div className="w-7 h-2 bg-gray-200 rounded" />
                </div>
            ))}
        </div>
    </div>
);

const getPageWindow = (current: number, total: number): (number | '...')[] => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: (number | '...')[] = [1];
    if (current > 3) pages.push('...');
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (current < total - 2) pages.push('...');
    pages.push(total);

    return pages;
};

const AppointmentList = ({ appointmentData, loading, update, setUpdate, totalPages, handlePageChange, currentPage, total }: any) => {
    const handlePageChangeAndScroll = (page: number) => {
        handlePageChange(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const pages = getPageWindow(currentPage, totalPages);

    return (
        <div>
            {!loading && total > 0 && (
                <p className="text-xs font-semibold text-gray-500 mb-2">
                    Total: <span className="text-[#12AB81]">{total}</span>
                </p>
            )}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
                </div>
            ) : appointmentData?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {appointmentData.map((appointment: any, index: number) => (
                        <Card key={index} data={appointment} update={update} setUpdate={setUpdate} />
                    ))}
                </div>
            ) : (
                <NoData />
            )}

            {!loading && totalPages > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-6">
                    <button
                        onClick={() => handlePageChangeAndScroll(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-default active:bg-gray-50"
                        aria-label="Previous page"
                    >
                        <MdChevronLeft size={20} />
                    </button>

                    {pages.map((page, i) =>
                        page === '...' ? (
                            <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-gray-400 text-sm">
                                ···
                            </span>
                        ) : (
                            <button
                                key={page}
                                onClick={() => handlePageChangeAndScroll(page as number)}
                                disabled={currentPage === page}
                                className={`w-9 h-9 rounded-xl text-sm font-semibold transition-colors
                                    ${currentPage === page
                                        ? 'bg-[#12AB81] text-white cursor-default'
                                        : 'bg-white border border-gray-200 text-gray-600 active:bg-gray-50'
                                    }`}
                            >
                                {page}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => handlePageChangeAndScroll(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-30 disabled:cursor-default active:bg-gray-50"
                        aria-label="Next page"
                    >
                        <MdChevronRight size={20} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default AppointmentList;
